import type { GridItemProps } from '@tvg/fuse-design/responsive-grid';
import type { ProductCardProps as FuseProductCardProps } from '@tvg/fuse-design/simple-product-card';
import useConfigContext from 'client/context/config';
import { useCriteoLoadEvent } from 'client/hooks/criteoEvents';
import { getProductProviderProps } from 'client/utils/providerProps';
import { clearText } from 'client/utils/textModifications';
import React, { useMemo } from 'react';
import { RecProduct } from 'server/services/recommendation/types';
import { StyledProductCard } from './Carousel.css';

export interface CarouselProductCardProps
  extends Partial<FuseProductCardProps> {
  product?: RecProduct;
  isSkeleton?: boolean;
  gridItemProps?: Partial<GridItemProps>;
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
}

function CarouselProductCard({ product, ...props }: CarouselProductCardProps) {
  const { provider } = useConfigContext();
  useCriteoLoadEvent(product?.onLoadBeacon);

  const titleMemo = useMemo(
    () => clearText(product?.title ?? ''),
    [product?.title],
  );

  const brandMemo = useMemo(
    () => clearText(product?.brand ?? ''),
    [product?.brand],
  );

  const imageAltTextMemo = useMemo(
    () => clearText(`Image of ${product?.brand ?? ''} ${product?.title}`),
    [product?.brand, product?.title],
  );

  return (
    <StyledProductCard
      brand={brandMemo}
      title={titleMemo}
      imgUrl={product?.image.url}
      imgAltText={imageAltTextMemo}
      prodUrl={product?.url}
      price={product?.price.current}
      prevPrice={product?.price.previous}
      isEuro={product?.price.currencyCode === 'EUR'}
      {...props}
      {...getProductProviderProps({ provider, product }, props)}
    />
  );
}

export default CarouselProductCard;
